import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find(c => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl" />
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">该漫画可能已被下架或链接无效</p>
            <Link to="/" className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter(c => c.slug !== comic.slug && (c.category === comic.category || c.tags.some(t => comic.tags.includes(t))))
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: `${comic.title} - 布丁漫画`,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://puddingcomic.com/article/${comic.slug}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: Math.floor(comic.views / 100),
    },
    genre: comic.tags,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。布丁漫画免费韩国漫画在线阅读，${comic.tags.join('、')}等类型韩漫免费阅读。`}
        keywords={`布丁漫画,${comic.title},韩国漫画,${comic.tags.join(',')},免费韩漫在线阅读`}
        canonicalUrl={`https://puddingcomic.com/article/${comic.slug}`}
        ogTitle={`${comic.title} - 免费韩国漫画在线阅读 | 布丁漫画`}
        ogDescription={comic.description}
        schema={articleSchema}
      />
      <Navbar />

      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: `${categories.find(c => c.slug === comic.category)?.name || ''}漫画`, to: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 lg:px-10 py-6">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 mx-auto lg:mx-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 免费韩国漫画`}
                  title={`${comic.title} - 布丁漫画正版韩漫在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                <span className="text-foreground-300">|</span>
                <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-primary-500' : 'text-foreground-500'}`}>
                  {comic.status}
                </span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-600">共 <strong>{comic.chapters}</strong> 话</span>
                <span className="text-foreground-300">|</span>
                <span className="flex items-center gap-1 text-sm">
                  <span className="text-secondary-500 font-semibold">{comic.rating}</span>
                  <i className="ri-star-fill text-secondary-400 text-xs" />
                  <span className="text-foreground-500">分</span>
                </span>
                <span className="text-foreground-300">|</span>
                <span className="text-sm text-foreground-500">{(comic.views / 10000).toFixed(0)}万次阅读</span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                  <i className="ri-play-circle-line mr-2" />
                  开始阅读 第1话
                </button>
                <button className="inline-flex items-center px-5 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap border border-background-200 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-heart-line mr-2" />
                  收藏
                </button>
                <button className="inline-flex items-center px-5 py-3 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap border border-background-200 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  <i className="ri-share-line mr-2" />
                  分享
                </button>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        title={`${related.title} - 免费韩国漫画在线阅读`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
                        {related.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{related.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{related.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}